import React, { useMemo } from 'react'
import {
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Badge,
  Card,
  CardMedia,
  CardContent,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  Visibility,
  Favorite,
  FavoriteBorder,
  Share,
  Download,
  Verified,
  Lock,
  Public,
  Schedule,
  ColorLens,
  Palette,
  AutoAwesome,
  Pattern,
  PhotoLibrary,
  GridView,
  Edit,
} from '@mui/icons-material'
import ReactTimeago from 'react-timeago'
import { useNavigate } from 'react-router-dom'
import { Topic } from '@/types'
import { TopicType, TopicTypes } from '@/utils/constants'
import { UserAvatar } from '@/components'
import { useLocalStorage } from '@/hooks/common'
import { convertToGMT, getTopicNameFromTopic, isBabylonType } from '@/utils/helper'
import { IMAGES } from '@/assets/images'
import { useInteractToNote } from '@/hooks/api/dashboard/useInteractToNote'
import { toast } from 'react-hot-toast'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import CustomTimeAgo from '@/components/CustomTimeAgo'

interface ModernTopicCardProps {
  topic: Topic
  onClick: () => void
  onRemoved?: () => void
  showStats?: boolean
  showActions?: boolean
}

// Stripe-level card design with premium aesthetics
const ModernCard = styled(Card)(() => ({
  position: 'relative',
  borderRadius: '16px',
  backgroundColor: '#ffffff',
  border: '1px solid rgba(226, 232, 240, 0.8)',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '16px',
    padding: '1px',
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1))',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.12), 0 4px 16px rgba(15, 23, 42, 0.08)',
    borderColor: 'rgba(99, 102, 241, 0.2)',
  },

  '&:hover::before': {
    opacity: 1,
  },
}))

const StyledCardMedia = styled(CardMedia)(() => ({
  height: 240,
  position: 'relative',
  backgroundColor: '#f8fafc',
  overflow: 'hidden',

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '40%',
    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, transparent 100%)',
    opacity: 0,
    transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover::after': {
    opacity: 1,
  },
}))

const FloatingActions = styled(Box)(() => ({
  position: 'absolute',
  top: 14,
  right: 14,
  display: 'flex',
  gap: 10,
  opacity: 0,
  transform: 'translateY(-8px)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 2,

  '.MuiCard-root:hover &': {
    opacity: 1,
    transform: 'translateY(0)',
  },
}))

const ActionButton = styled(IconButton)(() => ({
  width: 44,
  height: 44,
  backgroundColor: 'rgba(255, 255, 255, 0.92)',
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.4)',
  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12), 0 2px 4px rgba(15, 23, 42, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    backgroundColor: '#ffffff',
    transform: 'translateY(-2px) scale(1.05)',
    boxShadow: '0 8px 20px rgba(99, 102, 241, 0.15), 0 4px 8px rgba(15, 23, 42, 0.1)',
    borderColor: 'rgba(99, 102, 241, 0.2)',
  },

  '&:active': {
    transform: 'translateY(0) scale(0.98)',
  },

  '& .MuiSvgIcon-root': {
    fontSize: 20,
    color: '#64748b',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover .MuiSvgIcon-root': {
    color: '#6366f1',
  },
}))

const StatsOverlay = styled(Box)(() => ({
  position: 'absolute',
  bottom: 14,
  left: 14,
  right: 14,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  opacity: 0,
  transform: 'translateY(12px)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 2,

  '.MuiCard-root:hover &': {
    opacity: 1,
    transform: 'translateY(0)',
  },
}))

const StatChip = styled(Chip)(() => ({
  backgroundColor: 'rgba(255, 255, 255, 0.92)',
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.4)',
  borderRadius: '10px',
  fontSize: '12px',
  fontWeight: 600,
  height: 32,
  color: '#475569',
  letterSpacing: '-0.01em',
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
  },

  '& .MuiChip-icon': {
    fontSize: 16,
    marginLeft: 6,
    color: '#64748b',
  },

  '& .MuiChip-label': {
    paddingLeft: '8px',
    paddingRight: '12px',
  },
}))

const EnhancedCardContent = styled(CardContent)(() => ({
  padding: '24px',
  paddingTop: '20px',
}))

const TitleTypography = styled(Typography)(() => ({
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  fontSize: '17px',
  fontWeight: 700,
  lineHeight: '24px',
  color: '#0f172a',
  letterSpacing: '-0.02em',
  marginBottom: '12px',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '.MuiCard-root:hover &': {
    color: '#6366f1',
  },
}))

const MetaRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
}))

const UserSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
}))

const StatsSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: '16px',
  marginTop: '4px',
  borderTop: '1px solid rgba(226, 232, 240, 0.6)',
}))

const ModernTopicCard: React.FC<ModernTopicCardProps> = ({
  topic,
  onClick,
  onRemoved,
  showStats = true,
  showActions = true,
}) => {
  const [ewUser] = useLocalStorage('ewUser', null)
  const interactToNote = useInteractToNote()
  const navigate = useNavigate()

  // Get updated topic data from store (this gets updated by useInteractToNote)
  const [topicList] = useAppStore((state) => [state.home.topicList], shallow)
  const updatedTopic = topicList[topic.topicID] || topic

  // Dynamic data exactly like the original API response
  const viewCount = Number(updatedTopic.visitorVisits || 0) + Number(updatedTopic.ownerVisits || 0)
  const likeCount = Number(updatedTopic.totalLikes || 0)
  const isOwner = ewUser?.userID === updatedTopic.userID

  // EXACT same logic as preview page
  const firstNote = useMemo(() => {
    const currentTopic = topicList[updatedTopic.topicID]
    if (currentTopic) {
      if (currentTopic.firstNote && currentTopic?.notes?.length > 0) {
        const note = currentTopic.notes.find((e) => e.noteID == currentTopic.firstNote.noteID)
        return note
      } else {
        return currentTopic.firstNote
      }
    }
  }, [updatedTopic.topicID, topicList])

  // Check like state - handle both API response (thumbsUp) and updated state (isThumbsUp)
  const isLiked =
    firstNote?.isThumbsUp == 1 ||
    (firstNote?.isThumbsUp === undefined && Number(firstNote?.thumbsUp) == 1)

  // Debug: Check if like state is updating
  console.log(
    `Topic ${updatedTopic.topicID}: isLiked=${isLiked}, isThumbsUp=${firstNote?.isThumbsUp}, thumbsUp=${firstNote?.thumbsUp}`,
  )

  // Get enhanced preview image
  const getPreviewImage = () => {
    if (isBabylonType(topic.topicType)) {
      return topic.firstNote?.egoList?.length
        ? topic.firstNote.egoList[0].photoAlbumURL
        : IMAGES.topicPreviewPlaceholder
    }
    return (topic.latestNote?.egoList || [])[0]?.photoAlbumURL || IMAGES.topicPreviewPlaceholder
  }

  // Get project type with premium Stripe-level styling
  const getProjectTypeChip = () => {
    const topicData = TopicTypes[Number(topic.topicType)]
    if (!topicData) return null

    const colors: Record<number, { bg: string; color: string; border: string; icon: JSX.Element }> =
      {
        [TopicType.PATTERN]: {
          bg: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(168, 85, 247, 0.1))',
          color: '#7c3aed',
          border: 'rgba(139, 92, 246, 0.2)',
          icon: <Pattern />,
        },
        [TopicType.ALBUM]: {
          bg: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1), rgba(219, 39, 119, 0.1))',
          color: '#ec4899',
          border: 'rgba(236, 72, 153, 0.2)',
          icon: <PhotoLibrary />,
        },
        [TopicType.GALLERY]: {
          bg: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(22, 163, 74, 0.1))',
          color: '#16a34a',
          border: 'rgba(34, 197, 94, 0.2)',
          icon: <GridView />,
        },
      }

    const style = colors[topicData.type] || {
      bg: 'linear-gradient(135deg, rgba(148, 163, 184, 0.1), rgba(100, 116, 139, 0.1))',
      color: '#64748b',
      border: 'rgba(148, 163, 184, 0.2)',
      icon: <Schedule />,
    }

    return (
      <Chip
        icon={style.icon}
        label={topicData.title}
        size='small'
        sx={{
          background: style.bg,
          color: style.color,
          border: `1px solid ${style.border}`,
          fontWeight: 600,
          fontSize: '12px',
          height: 32,
          borderRadius: '10px',
          letterSpacing: '-0.01em',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '& .MuiChip-icon': {
            fontSize: 16,
            color: style.color,
            marginLeft: '8px',
          },
          '& .MuiChip-label': {
            paddingLeft: '8px',
            paddingRight: '12px',
          },
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          },
        }}
      />
    )
  }

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (firstNote) {
      interactToNote.mutateAsync({
        interactionType: '3',
        interaction: firstNote.isThumbsUp == 1 ? '0' : '1',
        comment: 'test',
        noteID: firstNote.noteID,
        topicID: updatedTopic.topicID,
      })
    }
  }

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (!firstNote) return

    // Create preview URL
    const previewUrl = `${window.location.origin}/preview?topicID=${updatedTopic.topicID}&topicType=${updatedTopic.topicType}&noteID=${firstNote.noteID}`

    // Copy to clipboard
    navigator.clipboard
      .writeText(previewUrl)
      .then(() => {
        toast.success('Link copied to clipboard!')
      })
      .catch(() => {
        toast.error('Failed to copy link')
      })
  }

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()

    // Albums navigate to preview, patterns to editor
    if (updatedTopic.topicType === TopicType.ALBUM.toString()) {
      navigate(`/preview?topicID=${updatedTopic.topicID}&topicType=${updatedTopic.topicType}`)
    } else {
      navigate(`/pattern?topicID=${updatedTopic.topicID}`)
    }
  }

  return (
    <ModernCard onClick={onClick}>
      <StyledCardMedia image={getPreviewImage()} title={getTopicNameFromTopic(topic)}>
        {/* Floating Actions */}
        {showActions && (
          <FloatingActions>
            {/* Edit button for owner - Premium gradient style */}
            {isOwner && (
              <Tooltip title='Edit' arrow>
                <IconButton
                  onClick={handleEdit}
                  size='small'
                  sx={{
                    width: 44,
                    height: 44,
                    background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                    backdropFilter: 'blur(16px)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                    boxShadow:
                      '0 4px 12px rgba(99, 102, 241, 0.25), 0 2px 4px rgba(15, 23, 42, 0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

                    '&:hover': {
                      background: 'linear-gradient(135deg, #818cf8 0%, #c084fc 100%)',
                      transform: 'translateY(-2px) scale(1.05)',
                      boxShadow:
                        '0 8px 20px rgba(99, 102, 241, 0.35), 0 4px 8px rgba(15, 23, 42, 0.1)',
                      borderColor: 'rgba(99, 102, 241, 0.4)',
                    },

                    '&:active': {
                      transform: 'translateY(0) scale(0.98)',
                    },

                    '& .MuiSvgIcon-root': {
                      fontSize: 20,
                      color: '#ffffff',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}

            {/* Like button - hidden for albums since interactions happen inside */}
            {updatedTopic.topicType !== TopicType.ALBUM.toString() && (
              <Tooltip title='Like' arrow>
                <ActionButton
                  onClick={handleLike}
                  size='small'
                  disabled={interactToNote.isLoading}
                  sx={{
                    opacity: interactToNote.isLoading ? 0.6 : 1,
                    pointerEvents: interactToNote.isLoading ? 'none' : 'auto',
                  }}
                >
                  {isLiked ? (
                    <Favorite
                      sx={{
                        color: '#ef4444 !important',
                        '&.MuiSvgIcon-root': { color: '#ef4444 !important' },
                      }}
                    />
                  ) : (
                    <FavoriteBorder
                      sx={{
                        color: '#64748b !important',
                        '&.MuiSvgIcon-root': { color: '#64748b !important' },
                      }}
                    />
                  )}
                </ActionButton>
              </Tooltip>
            )}

            {/* Share button - hidden for albums since sharing happens inside */}
            {updatedTopic.topicType !== TopicType.ALBUM.toString() && (
              <Tooltip title='Share' arrow>
                <ActionButton onClick={handleShare} size='small'>
                  <Share />
                </ActionButton>
              </Tooltip>
            )}
          </FloatingActions>
        )}

        {/* Premium Stats Overlay on Hover */}
        {showStats && (
          <StatsOverlay>
            <Box sx={{ display: 'flex', gap: 1.25 }}>
              <StatChip icon={<Visibility />} label={viewCount.toLocaleString()} size='small' />
              {/* Hide like chip for albums since interactions happen inside */}
              {updatedTopic.topicType !== TopicType.ALBUM.toString() && (
                <StatChip
                  icon={
                    <Favorite
                      sx={{
                        color: isLiked ? '#ef4444' : '#64748b',
                      }}
                    />
                  }
                  label={likeCount.toLocaleString()}
                  size='small'
                />
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                },
              }}
            >
              {updatedTopic.visibility === '3' ? (
                <Tooltip title='Private' arrow>
                  <Lock sx={{ color: '#64748b', fontSize: 18 }} />
                </Tooltip>
              ) : (
                <Tooltip title='Public' arrow>
                  <Public sx={{ color: '#22c55e', fontSize: 18 }} />
                </Tooltip>
              )}
            </Box>
          </StatsOverlay>
        )}
      </StyledCardMedia>

      <EnhancedCardContent>
        {/* Title */}
        <TitleTypography>{getTopicNameFromTopic(topic)}</TitleTypography>

        {/* Meta Row - Clean, no premium badges or progress bars */}
        <MetaRow>{getProjectTypeChip()}</MetaRow>

        {/* User Section - Premium styling */}
        <UserSection>
          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: '-3px',
                borderRadius: '50%',
                padding: '2px',
                background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              },
            }}
          >
            <UserAvatar
              borderRadius={50}
              name={updatedTopic.userName}
              url={updatedTopic.profilePicThumbURL}
              size={36}
            />
          </Box>

          <Typography
            variant='body2'
            sx={{
              fontWeight: 600,
              color: '#1e293b',
              fontSize: '14px',
              lineHeight: '18px',
              flex: 1,
              letterSpacing: '-0.01em',
            }}
          >
            {isOwner ? 'You' : updatedTopic.userName?.split(' ')[0]}
          </Typography>

          <Typography
            variant='caption'
            sx={{
              color: '#94a3b8',
              fontSize: '12px',
              fontWeight: 500,
              letterSpacing: '-0.01em',
            }}
          >
            <CustomTimeAgo date={updatedTopic.dateCreated || updatedTopic.dateUpdated} />
          </Typography>
        </UserSection>

        {/* Premium Stats Section */}
        {showStats && (
          <StatsSection>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  padding: '6px 10px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(248, 250, 252, 0.8)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(241, 245, 249, 1)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Visibility sx={{ fontSize: 16, color: '#6366f1' }} />
                <Typography
                  variant='caption'
                  sx={{
                    color: '#475569',
                    fontWeight: 600,
                    fontSize: '13px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {viewCount.toLocaleString()}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  padding: '6px 10px',
                  borderRadius: '8px',
                  backgroundColor: isLiked ? 'rgba(239, 68, 68, 0.08)' : 'rgba(248, 250, 252, 0.8)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor:
                    updatedTopic.topicType === TopicType.ALBUM.toString() ? 'default' : 'pointer',
                  ...(updatedTopic.topicType !== TopicType.ALBUM.toString() && {
                    '&:hover': {
                      backgroundColor: isLiked
                        ? 'rgba(239, 68, 68, 0.12)'
                        : 'rgba(241, 245, 249, 1)',
                      transform: 'translateY(-1px)',
                    },
                  }),
                }}
              >
                <Favorite
                  sx={{
                    fontSize: 16,
                    color: isLiked ? '#ef4444' : '#a855f7',
                  }}
                />
                <Typography
                  variant='caption'
                  sx={{
                    color: isLiked ? '#ef4444' : '#475569',
                    fontWeight: 600,
                    fontSize: '13px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {likeCount.toLocaleString()}
                </Typography>
              </Box>
            </Box>

            {/* Privacy indicator with premium styling */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: 'rgba(248, 250, 252, 0.8)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(241, 245, 249, 1)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              {updatedTopic.visibility === '3' ? (
                <Tooltip title='Private' arrow>
                  <Lock sx={{ color: '#64748b', fontSize: 16 }} />
                </Tooltip>
              ) : (
                <Tooltip title='Public' arrow>
                  <Public sx={{ color: '#22c55e', fontSize: 16 }} />
                </Tooltip>
              )}
            </Box>
          </StatsSection>
        )}
      </EnhancedCardContent>
    </ModernCard>
  )
}

export default ModernTopicCard
